'use client';

import { c as _c } from "react/compiler-runtime";
import React, { useCallback, useState } from 'react';
import { Table } from '../Table';
import { TableCard } from '../TableCard';
import { DataTableBody } from './DataTableBody';
import { DataTableBulkBar, DataTableBulkFloating, DataTableBulkHeader } from './DataTableBulkBar';
import { DataTableHeader } from './DataTableHeader';
import { DataTablePagination } from './DataTablePagination';
import { hasMultiplePages } from './paginationState';
import type { DataTableProps, SortState } from './types';
export function DataTable(t0) {
  const $ = _c(76);
  const {
    data,
    columns,
    getRowId,
    card,
    sort: controlledSort,
    onSortChange: controlledOnSortChange,
    defaultSort,
    selectionMode: t1,
    selectedIds,
    onSelectionChange,
    pagination,
    onPageChange,
    isLoading: t2,
    skeletonRowCount: t3,
    error,
    emptyState,
    emptyFilterState,
    hasActiveFilters: t4,
    onRowClick,
    rowClassName,
    rowActions,
    getRowActionLabel,
    initiallyOpenActionRowId,
    bulkActions,
    bulkActionsPosition: t5,
    density: t6,
    className
  } = t0;
  const selectionMode = t1 === undefined ? "none" : t1;
  const isLoading = t2 === undefined ? false : t2;
  const skeletonRowCount = t3 === undefined ? 5 : t3;
  const hasActiveFilters = t4 === undefined ? false : t4;
  const bulkActionsPosition = t5 === undefined ? "header" : t5;
  const density = t6 === undefined ? "default" : t6;
  const cellPaddingX = density === "compact" ? "px-4" : "px-6";
  let t7;
  if ($[0] !== defaultSort) {
    t7 = defaultSort ?? {
      key: "",
      direction: "asc"
    };
    $[0] = defaultSort;
    $[1] = t7;
  } else {
    t7 = $[1];
  }
  const [internalSort, setInternalSort] = useState(t7);
  const isControlledSort = controlledSort !== undefined && controlledOnSortChange !== undefined;
  const sort = isControlledSort ? controlledSort : internalSort;
  const onSortChange = isControlledSort ? controlledOnSortChange : setInternalSort;
  const hasRowActions = !!rowActions && rowActions.length > 0;
  const hasBulkActions = !!bulkActions && bulkActions.length > 0;
  const hasSelection = selectionMode === "multiple" && selectedIds && onSelectionChange;
  const selectionCount = selectedIds?.size ?? 0;
  const showBulkActions = hasBulkActions && selectionCount > 0;
  const allSelected = hasSelection ? data.length > 0 && selectionCount === data.length : false;
  let t8;
  if ($[2] !== allSelected || $[3] !== data || $[4] !== getRowId || $[5] !== onSelectionChange) {
    t8 = () => {
      if (!onSelectionChange) {
        return;
      }
      if (allSelected) {
        onSelectionChange(new Set());
      } else {
        onSelectionChange(new Set(data.map(getRowId)));
      }
    };
    $[2] = allSelected;
    $[3] = data;
    $[4] = getRowId;
    $[5] = onSelectionChange;
    $[6] = t8;
  } else {
    t8 = $[6];
  }
  const handleToggleSelectAll = t8;
  let t9;
  if ($[7] !== onSelectionChange) {
    t9 = () => {
      onSelectionChange?.(new Set());
    };
    $[7] = onSelectionChange;
    $[8] = t9;
  } else {
    t9 = $[8];
  }
  const handleClearSelection = t9;
  const showHeader = card !== undefined;
  let t10;
  if ($[9] !== onPageChange || $[10] !== pagination) {
    t10 = pagination && hasMultiplePages(pagination) && onPageChange;
    $[9] = onPageChange;
    $[10] = pagination;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  const showPagination = t10;
  const showFooterBulk = showBulkActions && bulkActionsPosition === "footer";
  const showHeaderBulk = showBulkActions && bulkActionsPosition === "header";
  const showFloatingBulk = showBulkActions && bulkActionsPosition === "floating";
  const t11 = `w-full pb-1 ${className ?? ""}`;
  const t12 = card?.className;
  let t13;
  if ($[12] !== bulkActions || $[13] !== card || $[14] !== handleClearSelection || $[15] !== selectedIds || $[16] !== showHeader || $[17] !== showHeaderBulk) {
    t13 = showHeader && <TableCard.Header title={card.title} badge={card.badge} actions={showHeaderBulk ? <DataTableBulkHeader selectedIds={selectedIds} bulkActions={bulkActions} onClearSelection={handleClearSelection} /> : card.headerActions} />;
    $[12] = bulkActions;
    $[13] = card;
    $[14] = handleClearSelection;
    $[15] = selectedIds;
    $[16] = showHeader;
    $[17] = showHeaderBulk;
    $[18] = t13;
  } else {
    t13 = $[18];
  }
  let t14;
  if ($[19] !== allSelected || $[20] !== cellPaddingX || $[21] !== columns || $[22] !== handleToggleSelectAll || $[23] !== hasRowActions || $[24] !== isLoading || $[25] !== onSortChange || $[26] !== selectionMode || $[27] !== sort) {
    t14 = <DataTableHeader columns={columns} sort={sort} onSortChange={onSortChange} selectionMode={selectionMode} allSelected={allSelected} onToggleSelectAll={handleToggleSelectAll} hasRowActions={hasRowActions} isLoading={isLoading} cellPaddingX={cellPaddingX} />;
    $[19] = allSelected;
    $[20] = cellPaddingX;
    $[21] = columns;
    $[22] = handleToggleSelectAll;
    $[23] = hasRowActions;
    $[24] = isLoading;
    $[25] = onSortChange;
    $[26] = selectionMode;
    $[27] = sort;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== cellPaddingX || $[30] !== columns || $[31] !== data || $[32] !== emptyFilterState || $[33] !== emptyState || $[34] !== error || $[35] !== getRowActionLabel || $[36] !== getRowId || $[37] !== hasActiveFilters || $[38] !== initiallyOpenActionRowId || $[39] !== isLoading || $[40] !== onRowClick || $[41] !== onSelectionChange || $[42] !== rowActions || $[43] !== rowClassName || $[44] !== selectedIds || $[45] !== selectionMode || $[46] !== skeletonRowCount) {
    t15 = <DataTableBody data={data} columns={columns} getRowId={getRowId} selectionMode={selectionMode} selectedIds={selectedIds} onSelectionChange={onSelectionChange} isLoading={isLoading} skeletonRowCount={skeletonRowCount} error={error} emptyState={emptyState} emptyFilterState={emptyFilterState} hasActiveFilters={hasActiveFilters} onRowClick={onRowClick} rowClassName={rowClassName} rowActions={rowActions} getRowActionLabel={getRowActionLabel} initiallyOpenActionRowId={initiallyOpenActionRowId} cellPaddingX={cellPaddingX} />;
    $[29] = cellPaddingX;
    $[30] = columns;
    $[31] = data;
    $[32] = emptyFilterState;
    $[33] = emptyState;
    $[34] = error;
    $[35] = getRowActionLabel;
    $[36] = getRowId;
    $[37] = hasActiveFilters;
    $[38] = initiallyOpenActionRowId;
    $[39] = isLoading;
    $[40] = onRowClick;
    $[41] = onSelectionChange;
    $[42] = rowActions;
    $[43] = rowClassName;
    $[44] = selectedIds;
    $[45] = selectionMode;
    $[46] = skeletonRowCount;
    $[47] = t15;
  } else {
    t15 = $[47];
  }
  let t16;
  if ($[48] !== t14 || $[49] !== t15) {
    t16 = <div className="overflow-x-auto"><Table header={t14}>{t15}</Table></div>;
    $[48] = t14;
    $[49] = t15;
    $[50] = t16;
  } else {
    t16 = $[50];
  }
  let t17;
  if ($[51] !== bulkActions || $[52] !== handleClearSelection || $[53] !== selectedIds || $[54] !== showFooterBulk) {
    t17 = showFooterBulk && <DataTableBulkBar selectedIds={selectedIds} bulkActions={bulkActions} onClearSelection={handleClearSelection} />;
    $[51] = bulkActions;
    $[52] = handleClearSelection;
    $[53] = selectedIds;
    $[54] = showFooterBulk;
    $[55] = t17;
  } else {
    t17 = $[55];
  }
  let t18;
  if ($[56] !== isLoading || $[57] !== onPageChange || $[58] !== pagination || $[59] !== showPagination) {
    t18 = showPagination && <TableCard.Footer><DataTablePagination pagination={pagination} onPageChange={onPageChange} isLoading={isLoading} /></TableCard.Footer>;
    $[56] = isLoading;
    $[57] = onPageChange;
    $[58] = pagination;
    $[59] = showPagination;
    $[60] = t18;
  } else {
    t18 = $[60];
  }
  let t19;
  if ($[61] !== t12 || $[62] !== t13 || $[63] !== t16 || $[64] !== t17 || $[65] !== t18) {
    t19 = <TableCard className={t12}>{t13}{t16}{t17}{t18}</TableCard>;
    $[61] = t12;
    $[62] = t13;
    $[63] = t16;
    $[64] = t17;
    $[65] = t18;
    $[66] = t19;
  } else {
    t19 = $[66];
  }
  let t20;
  if ($[67] !== bulkActions || $[68] !== handleClearSelection || $[69] !== selectedIds || $[70] !== showFloatingBulk) {
    t20 = showFloatingBulk && <DataTableBulkFloating selectedIds={selectedIds} bulkActions={bulkActions} onClearSelection={handleClearSelection} />;
    $[67] = bulkActions;
    $[68] = handleClearSelection;
    $[69] = selectedIds;
    $[70] = showFloatingBulk;
    $[71] = t20;
  } else {
    t20 = $[71];
  }
  let t21;
  if ($[72] !== t11 || $[73] !== t19 || $[74] !== t20) {
    t21 = <div className={t11}>{t19}{t20}</div>;
    $[72] = t11;
    $[73] = t19;
    $[74] = t20;
    $[75] = t21;
  } else {
    t21 = $[75];
  }
  return t21;
}